import { useEffect } from 'react';

/**
 * Lightweight SEO hook — updates document.title, meta description, canonical
 * and Open Graph tags directly on mount. No React-Helmet dependency.
 *
 * Only call this on PUBLIC pages (no auth wall) so search engines index the
 * right content. Protected pages should leave the global defaults from
 * index.html in place.
 */
export default function useSeo({ title, description, canonical, ogImage, jsonLd, noindex } = {}) {
  useEffect(() => {
    const prevTitle = document.title;

    if (title) {
      document.title = title;
    }

    const setMeta = (selector, attr, value) => {
      if (!value) return null;
      let el = document.head.querySelector(selector);
      const created = !el;
      if (!el) {
        el = document.createElement('meta');
        const [, name] = selector.match(/\[(?:name|property)="([^"]+)"\]/) || [];
        if (selector.includes('property=')) {
          el.setAttribute('property', name);
        } else if (name) {
          el.setAttribute('name', name);
        }
        document.head.appendChild(el);
      }
      const prev = el.getAttribute(attr);
      el.setAttribute(attr, value);
      return { el, prev, created };
    };

    const setLink = (rel, href) => {
      if (!href) return null;
      let el = document.head.querySelector(`link[rel="${rel}"]`);
      const created = !el;
      if (!el) {
        el = document.createElement('link');
        el.setAttribute('rel', rel);
        document.head.appendChild(el);
      }
      const prev = el.getAttribute('href');
      el.setAttribute('href', href);
      return { el, prev, created };
    };

    const restorers = [];

    const desc = setMeta('meta[name="description"]', 'content', description);
    if (desc) restorers.push(() => (desc.created ? desc.el.remove() : desc.el.setAttribute('content', desc.prev || '')));

    const ogTitle = setMeta('meta[property="og:title"]', 'content', title);
    if (ogTitle) restorers.push(() => (ogTitle.created ? ogTitle.el.remove() : ogTitle.el.setAttribute('content', ogTitle.prev || '')));

    const ogDesc = setMeta('meta[property="og:description"]', 'content', description);
    if (ogDesc) restorers.push(() => (ogDesc.created ? ogDesc.el.remove() : ogDesc.el.setAttribute('content', ogDesc.prev || '')));

    const ogUrl = setMeta('meta[property="og:url"]', 'content', canonical);
    if (ogUrl) restorers.push(() => (ogUrl.created ? ogUrl.el.remove() : ogUrl.el.setAttribute('content', ogUrl.prev || '')));

    const ogImg = setMeta('meta[property="og:image"]', 'content', ogImage);
    if (ogImg) restorers.push(() => (ogImg.created ? ogImg.el.remove() : ogImg.el.setAttribute('content', ogImg.prev || '')));

    const twTitle = setMeta('meta[name="twitter:title"]', 'content', title);
    if (twTitle) restorers.push(() => (twTitle.created ? twTitle.el.remove() : twTitle.el.setAttribute('content', twTitle.prev || '')));

    const twDesc = setMeta('meta[name="twitter:description"]', 'content', description);
    if (twDesc) restorers.push(() => (twDesc.created ? twDesc.el.remove() : twDesc.el.setAttribute('content', twDesc.prev || '')));

    const canon = setLink('canonical', canonical);
    if (canon) restorers.push(() => (canon.created ? canon.el.remove() : canon.el.setAttribute('href', canon.prev || '')));

    // Robots meta — set to noindex,follow on auth-walled / non-canonical pages
    if (noindex !== undefined) {
      const robotsValue = noindex ? 'noindex, follow' : 'index, follow, max-image-preview:large';
      const robots = setMeta('meta[name="robots"]', 'content', robotsValue);
      if (robots) restorers.push(() => (robots.created ? robots.el.remove() : robots.el.setAttribute('content', robots.prev || '')));
    }

    // JSON-LD structured data — inject one or more <script type="application/ld+json"> tags
    const ldList = Array.isArray(jsonLd) ? jsonLd : (jsonLd ? [jsonLd] : []);
    const ldEls = ldList.map((ld, i) => {
      try {
        const el = document.createElement('script');
        el.type = 'application/ld+json';
        el.setAttribute('data-seo-page', `${i}`);
        el.text = JSON.stringify(ld);
        document.head.appendChild(el);
        return el;
      } catch {
        return null;
      }
    }).filter(Boolean);
    restorers.push(() => ldEls.forEach(el => el.remove()));

    return () => {
      document.title = prevTitle;
      restorers.forEach(fn => fn());
    };
  }, [title, description, canonical, ogImage, noindex, JSON.stringify(jsonLd)]);
}
